import type { Order, NewOrder, OrderLine, DbOrderLine, OrderCustomer } from 'src/types/types';

import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { getGuid } from 'src/utils/utils';

import { useAppDispatch, useAppSelector } from 'src/hooks';
import { setAllOrders } from 'src/features/orderSlice/orderSlice';
import {
  createOrder,
  deleteOrder,
  getAllOrders,
  createOrderLines,
  deleteOrderLines,
} from 'src/data/api';

import { Form } from 'src/components/hook-form';

import { STATUS } from 'src/types/types';

import { InvoiceNewEditAddress } from './invoice-new-edit-address';
import { InvoiceNewEditDetails } from './invoice-new-edit-details';
import { InvoiceNewEditStatusDate } from './invoice-new-edit-status-date';

type Props = {
  order?: Order;
};

export function InvoiceNewEditForm({ order }: Props) {
  const products = useAppSelector((state) => state.products.products);
  const router = useRouter();
  const newOrderId = order?.id ?? getGuid();
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [deliveryPrice, setDeliveryPrice] = useState<null | number>(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(2, 'day'));
  const [customer, setCustomer] = useState<OrderCustomer | undefined>(undefined);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([]);
  const [hasDelivery, setHasDelivery] = useState<boolean>(false);
  const [hasMoms, setHasMoms] = useState<boolean>(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (products.length <= 0) return;
    if (!order) {
      setOrderLines([
        {
          name: '',
          price: 0,
          quantity: 1,
          orderLineId: getGuid(),
        },
      ]);
      return;
    }

    setDeliveryPrice(order.deliveryPrice);
    setStartDate(dayjs(order?.startDate * 1000));
    setEndDate(dayjs(order.endDate * 1000));
    setCustomer(order.customer);
    setCustomerId(order.customerId ?? null);
    setOrderLines(order.orderLines);
    setHasDelivery(order.hasDelivery);
    setHasMoms(order.hasMoms);
  }, [order, products]);

  const methods = useForm<Order>({
    mode: 'all',
    values: order,
  });

  const {
    formState: { isSubmitting },
  } = methods;

  const submit = async (draft?: boolean) => {
    if (!customer?.name) {
      toast.error('Der skal vælges en kunde, før der kan oprettes en ordre');
      return;
    }

    const lines = orderLines.map((orderLine) => ({
      name: orderLine.name,
      order: newOrderId,
      price: orderLine.price,
      product: orderLine.product?.id,
      quantity: orderLine.quantity,
    })) as DbOrderLine[];

    const body: NewOrder = {
      id: newOrderId,
      status: order?.status ?? (draft ? STATUS.KLADDE : STATUS.TILBUD),
      customerName: customer.name,
      customerPhone: customer.phone,
      customerAddress: customer.address || null,
      customerCompany: customer.company || null,
      customerCompanyCvr: customer.companyCvr || null,
      customerId,
      startDate: startDate.unix(),
      endDate: endDate.unix(),
      deliveryPrice,
      totalPrice: lines.reduce((sum, _order) => sum + _order.price * _order.quantity, 0),
      hasDelivery,
      note: null,
      paymentDate: order?.paymentDate ?? null,
      hasMoms,
    };

    try {
      if (order) {
        await deleteOrder(order?.id);
        await deleteOrderLines(order?.id);
      }
      await createOrder(body);
      await createOrderLines(lines);
      dispatch(setAllOrders(await getAllOrders()));
      toast.success(`Ordre blev ${order?.id ? 'opdateret' : 'oprettet'}. `);
      router.push(paths.admin.order.root);
    } catch (e) {
      console.log(e);
      toast.error('Kunne ikke oprette ordre');
    }
  };

  const getWashPrice = () => {
    // Finder vaskelinjen (fx 'Opvask')
    const washLineIndex = orderLines.findIndex((line) => line.name === 'Opvask');
    if (washLineIndex === -1) {
      // Hvis der ikke findes en vaskelinje, gør vi ikke noget
      return;
    }

    // Vi skal finde ordrelignende produkter, som IKKE er erstatninger
    const nonReplacementLines = orderLines.filter(
      (line) => line.product && line.product.category !== 'erstatning'
    );

    // Vi skal finde erstatningslinjer (erstatningsprodukter)
    const replacementLines = orderLines.filter(
      (line) => line.product && line.product.category === 'erstatning'
    );

    // For hver non-replacement linje finder vi den samlede quantity efter fratræk af erstatninger
    let totalWashPrice = 0;

    nonReplacementLines.forEach((line) => {
      // Find tilhørende erstatningslinje for dette produkt (hvis nogen)
      // Det kan gøres ved at matche line.product.id med erstatningsproduktets replacementProduct.id
      const matchingReplacementLine = replacementLines.find(
        (repLine) => repLine.product?.id === line.product?.replacementProduct?.id
      );

      const replacementQuantity = matchingReplacementLine ? matchingReplacementLine.quantity : 0;

      // Beregn vaskemængden: oprindelig quantity - erstatnings quantity (min 0)
      const effectiveQuantity = Math.max(line.quantity - replacementQuantity, 0);

      totalWashPrice += effectiveQuantity * (line.product?.washPrice || 0);
    });

    // Opdater vaskelinjen med ny pris
    const updatedLines = [...orderLines];
    updatedLines[washLineIndex] = {
      ...updatedLines[washLineIndex],
      price: totalWashPrice,
    };

    setOrderLines(updatedLines);
  };

  return (
    <Form methods={methods}>
      <Card>
        <InvoiceNewEditAddress
          setCustomer={(c) => setCustomer(c)}
          customer={customer}
          customerId={customerId}
          setCustomerId={setCustomerId}
        />

        <InvoiceNewEditStatusDate
          startDate={startDate}
          endDate={endDate}
          setEndDate={(_endDate) => setEndDate(_endDate)}
          setStartDate={(_startDate) => setStartDate(_startDate)}
          getWashPrice={getWashPrice}
          hasWash={orderLines.some((obj) => obj.name === 'Opvask')}
        />

        <InvoiceNewEditDetails
          setOrderLines={(value) => setOrderLines(value)}
          orderLines={orderLines}
          deliveryPrice={deliveryPrice}
          hasDelivery={hasDelivery}
          setDeliveryPrice={(price) => setDeliveryPrice(price)}
          setHasDelivery={(val) => setHasDelivery(val)}
          hasMoms={hasMoms}
          setHasMoms={setHasMoms}
          startDate={startDate}
          endDate={endDate}
          currentOrderId={order?.id}
        />
      </Card>

      <Box
        sx={{
          mt: 3,
          gap: 2,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          size="large"
          variant="outlined"
          loading={loadingSend && isSubmitting}
          onClick={() => submit(true)}
        >
          Opret kladde
        </Button>
        <Button
          size="large"
          variant="contained"
          loading={loadingSend && isSubmitting}
          onClick={() => submit()}
        >
          {order?.status ? 'Opdater' : 'Opret tilbud'}
        </Button>
      </Box>
    </Form>
  );
}
